import React, {FC, useCallback, useState} from 'react';
import * as dropzone from 'react-dropzone';
import styled, {css} from 'styled-components';
import IconButton from '../../Buttons/IconButton';
import Icon, {IconSize} from '../../Icon/Icon';
import {GenericIconName} from '../../Icon/IconName';
import {
  backgroundColor,
  bordersColor,
  complementaryColor,
  darkGrayColor,
  inactiveColor,
  notificationErrorColor,
  primaryColor,
} from '../../constants/colors';
import {fsXS, fsXXS, fwMediumBold} from '../../constants/typography';
import {
  inputElementBorderRadius,
  inputElementHeight,
  inputElementPadding,
} from './constants';

const Wrapper = styled.div<{showDropZone: boolean}>`
  display: flex;
  flex-direction: column;
  border-radius: ${inputElementBorderRadius}px;
  border: solid 1px transparent;
  ${props =>
    props.showDropZone &&
    css`
      border-color: ${bordersColor};
    `}

  &:focus {
    outline: none;
    border-color: ${bordersColor};
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  min-width: 0%;
`;

const Subline = styled.div`
  font-size: ${fsXXS}px;
  ${fwMediumBold};
  line-height: 1.33;
  letter-spacing: normal;
  color: ${darkGrayColor};
  margin-top: 8px;
`;

const SelectedFileWrapper = styled.div<{hasError: boolean}>`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: ${inputElementPadding}px;
  box-sizing: border-box;
  height: ${inputElementHeight}px;
  border-radius: ${inputElementBorderRadius}px;
  background-color: ${backgroundColor};
  box-shadow: inset 0 -1px 0 0 ${bordersColor};
  &:last-of-type {
    box-shadow: none;
  }
  border: 1px solid;
  border-color: ${props =>
    props.hasError ? notificationErrorColor : backgroundColor};
`;

const LabelText = styled.div`
  height: 20px;
  font-size: ${fsXS}px;
  ${fwMediumBold};
  line-height: 1.43;
  color: ${primaryColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AttachmentIcon = styled(Icon)``;

const FileInputWrapper = styled.label<{disabled: boolean}>`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: ${inputElementPadding}px;
  box-sizing: border-box;
  height: ${inputElementHeight}px;
  border-radius: ${inputElementBorderRadius}px;
  border: dashed 1px ${bordersColor};
  background-color: ${complementaryColor};

  &:not(:first-child) {
    margin-top: 16px;
  }

  &:hover {
    border-color: ${primaryColor};
  }

  ${props =>
    props.disabled &&
    css`
      cursor: default;
      border-color: ${inactiveColor};
      &:hover {
        border-color: ${inactiveColor};
      }
      ${LabelText} {
        color: ${inactiveColor};
      }
      ${AttachmentIcon} {
        fill: ${inactiveColor};
      }
    `}
`;

const HiddenInput = styled.input`
  display: none;
`;

interface Props {
  label?: string;
  subline?: string;
  className?: string;
  onChange?: (files: File[]) => void;
  initialFiles?: File[];
  accept?: string;
  maxFiles?: number;
  maxFileSizeInByte?: number;
}

const MAX_FILES = 3;
const MAX_FILE_SIZE_IN_BYTE = 3 * 1024 * 1024;

const FileInput: FC<Props> = function FileInput({
  label,
  subline,
  className,
  initialFiles,
  onChange,
  accept,
  maxFiles = MAX_FILES,
  maxFileSizeInByte = MAX_FILE_SIZE_IN_BYTE,
}) {
  const [files, setFiles] = useState<File[]>(initialFiles ?? []);
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const remainingFilesToAdd = maxFiles - files.length;

      if (remainingFilesToAdd > 0) {
        let nextFiles: File[];
        if (acceptedFiles.length > remainingFilesToAdd) {
          nextFiles = [
            ...files,
            ...acceptedFiles.slice(0, remainingFilesToAdd),
          ];
        } else {
          nextFiles = [...files, ...acceptedFiles];
        }
        setFiles(nextFiles);
        onChange?.(nextFiles);
      }
    },
    [files, maxFiles, onChange],
  );

  const removeFile = useCallback(
    (index: number) => {
      const nextFiles = [...files];
      nextFiles.splice(index, 1);
      setFiles(nextFiles);
      onChange?.(nextFiles);
    },
    [files, onChange],
  );

  const disabled = files.length >= maxFiles;

  const {getRootProps, getInputProps, isDragActive} = dropzone.useDropzone({
    onDrop,
    disabled,
    accept,
  });
  return (
    <Wrapper
      {...(getRootProps({
        onClick: e => {
          e.preventDefault();
        },
      }) as any)}
      showDropZone={isDragActive}
    >
      <div>
        {files.map((file, index) => (
          <SelectedFileWrapper
            hasError={file.size > maxFileSizeInByte}
            key={`${file.name}-${index}`}
          >
            <Row>
              <Icon asset={GenericIconName.IcFile} size={IconSize.S} />
              <LabelText>{file.name}</LabelText>
            </Row>
            <IconButton
              onClick={e => {
                e.stopPropagation();
                removeFile(index);
              }}
            >
              <Icon asset={GenericIconName.IcClose} size={IconSize.S} />
            </IconButton>
          </SelectedFileWrapper>
        ))}
      </div>
      <FileInputWrapper disabled={disabled} className={className}>
        <LabelText>{label}</LabelText>
        <AttachmentIcon
          asset={GenericIconName.IcAttachment}
          size={IconSize.S}
        />
        <HiddenInput
          {...(getInputProps() as any)}
          type="file"
          disabled={disabled}
          accept={accept}
        />
      </FileInputWrapper>
      <Subline>{subline}</Subline>
    </Wrapper>
  );
};

export default FileInput;
